import styled from 'styled-components';

import CustomIcon, { SeturIcon } from '@components/utils/CustomIcon';
import { Col, Row } from '@components/formStyles';
import Price from '@components/ui/price';
import { TransferSaleMethod, TransferVehicleType } from '@api/index';
import { BusIcon, CarIcon, InfoIcon, MinibusIcon } from '@assets/icons';
import { useDevice } from '@contexts/device';
import Typography from '@components/Typography';
import { HotelTransferSearchViewModel } from '@api/v3';
import Tooltip from '@components/ui/Tooltip/next';
import palette from '@utils/theme/palette';
import Markdown from '@components/utils/Markdown';

const Container = styled.div`
  border: 2px solid ${({ theme }) => theme.palette.gray87};
  border-radius: 8px;
  padding: 12px;
  width: 100%;
`;

const IconWrapper = styled.div`
  margin-right: 20px;
  align-self: center;
`;

const RightContainer = styled.div`
  display: flex;
  align-items: center;
`;

export type TransferItemProps = {
  count: number;
  isRoundTrip: boolean;
  guestCount: number;
} & HotelTransferSearchViewModel;

export const VehicleTypeIconMap: Record<TransferVehicleType, SeturIcon> = {
  [TransferVehicleType.BUS]: BusIcon,
  [TransferVehicleType.MINIBUS]: MinibusIcon,
  [TransferVehicleType.MIDIBUS]: MinibusIcon,
  [TransferVehicleType.MINI_S]: CarIcon,
  [TransferVehicleType.VIP]: CarIcon,
  [TransferVehicleType.OTOMOBIL]: CarIcon,
  [TransferVehicleType.ECO_TRANSFER]: CarIcon,
  [TransferVehicleType.FREE_TRANSFER]: CarIcon,
  [TransferVehicleType.MASTER_CARD_FREE_VIP_TRANSFER]: CarIcon,
};

const TransferItem = ({
  oneWaySalePrice,
  roundTripSalePrice,
  vehicleType,
  count,
  saleCurrency,
  isRoundTrip,
  saleMethodType,
  vehicleMaxPersonCount,
  transferVehicleType,
}: TransferItemProps) => {
  const { isDesktop } = useDevice();
  const tripTypePrice = isRoundTrip ? roundTripSalePrice * 2 : oneWaySalePrice;
  let price = 0;
  let totalReservedCarCount = 0;

  if (saleMethodType === TransferSaleMethod.ARA_SAY_S_) {
    if (vehicleMaxPersonCount && count) {
      totalReservedCarCount =
        Math.floor(count / vehicleMaxPersonCount) +
        (count % vehicleMaxPersonCount === 0 ? 0 : 1);

      price = totalReservedCarCount * tripTypePrice;
    }
  } else {
    totalReservedCarCount = count;
    price = count * tripTypePrice;
  }

  const vehicleIcon = vehicleType && VehicleTypeIconMap[vehicleType];
  return (
    <Container>
      {isDesktop ? (
        <Row justifyContent='space-between'>
          <Row>
            {vehicleIcon ? (
              <IconWrapper>
                <CustomIcon
                  icon={vehicleIcon}
                  fontSize={24}
                  paletteColor='D500'
                />
              </IconWrapper>
            ) : null}
            <Col justifyContent='center'>
              <Typography variant='caption' weight='Bold' color='D500'>
                {vehicleType}
              </Typography>
              <div>{vehicleMaxPersonCount + ' Kişilik'}</div>
            </Col>
          </Row>
          <RightContainer>
            {vehicleType === TransferVehicleType.ECO_TRANSFER &&
            transferVehicleType?.description ? (
              <Tooltip
                overlayInnerStyle={{
                  backgroundColor: palette.white,
                  color: palette.darkGray,

                  lineHeight: 1.43,
                  width: '350px',
                  boxShadow: ' 0px 0px 20px rgba(58, 58, 58, 0.15)',
                  borderRadius: '8px',
                  fontSize: '14px',
                }}
                placement='bottomRight'
                arrowColor={palette.white}
                title={
                  <Markdown
                    segment={true}
                    text={transferVehicleType?.description}
                  />
                }
              >
                <CustomIcon
                  icon={InfoIcon}
                  fontSize={24}
                  paletteColor='persianRed'
                />
              </Tooltip>
            ) : null}
            <Col style={{ marginLeft: 10, alignItems: 'flex-end' }}>
              <Price currency={saleCurrency} price={price} fontSize={36} />
              <div>
                {totalReservedCarCount +
                  (saleMethodType === TransferSaleMethod.ARA_SAY_S_
                    ? ' Araç '
                    : ' Kişi ') +
                  'Fiyatı'}
              </div>
            </Col>
          </RightContainer>
        </Row>
      ) : (
        <Col>
          <Row alignItems='center'>
            {vehicleIcon ? (
              <IconWrapper>
                <CustomIcon
                  icon={vehicleIcon}
                  fontSize={24}
                  paletteColor='D500'
                />
              </IconWrapper>
            ) : null}
            <Row
              alignItems='center'
              justifyContent='space-between'
              style={{ flex: 1 }}
            >
              <Col>
                <Typography variant='caption' weight='Bold' color='D400'>
                  {vehicleType}
                </Typography>
                <div>{vehicleMaxPersonCount + ' Kişilik'}</div>
              </Col>
              <RightContainer>
                {vehicleType === TransferVehicleType.ECO_TRANSFER &&
                transferVehicleType?.description ? (
                  <Tooltip
                    overlayInnerStyle={{
                      backgroundColor: palette.white,
                      color: palette.darkGray,

                      lineHeight: 1.43,
                      width: '250px',
                      boxShadow: ' 0px 0px 20px rgba(58, 58, 58, 0.15)',
                      borderRadius: '8px',
                      fontSize: '14px',
                    }}
                    placement='bottomRight'
                    arrowColor={palette.white}
                    title={
                      <Markdown
                        segment={true}
                        text={transferVehicleType?.description}
                      />
                    }
                  >
                    <CustomIcon
                      icon={InfoIcon}
                      fontSize={24}
                      paletteColor='persianRed'
                    />
                  </Tooltip>
                ) : null}
                <Col style={{ marginLeft: 10 }}>
                  <Price currency={saleCurrency} price={price} fontSize={36} />
                  {totalReservedCarCount +
                    (saleMethodType === TransferSaleMethod.ARA_SAY_S_
                      ? ' Araç '
                      : ' Kişi ') +
                    'Fiyatı'}
                </Col>
              </RightContainer>
            </Row>
          </Row>
        </Col>
      )}
    </Container>
  );
};

export default TransferItem;
